import { useCallback, useEffect, useState } from 'react'
import { FileOutput, X } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { exportLv, ladeAusgabe, previewLvExport, type LvCheckCode, type LvExportOptionen, type LvExportVorschau } from '../api/calculation-lv.api'
import type { Calculation } from '../types/calculation.types'
import { PRUEFUNG } from '../lib/lv-pruefung'

const EBENE: Record<string, string> = { LV: 'auf die LV-Summe', HG: 'je Hauptgruppe', OG: 'je Obergruppe', LG: 'je Leistungsgruppe', ULG: 'je Unterleistungsgruppe' }
const heute = () => new Date().toISOString().slice(0, 10)
const prozent = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Was der Server bei einem Abbruch mitschickt (3.8): Abweichungen der Selbstprüfung, Fehler der Schemaprüfung. */
function fehlerDetails(e: unknown): string[] {
  const d = (e as { response?: { data?: { deviations?: { oz: string; field: string; expected: unknown; actual: unknown }[]; errors?: { line: number | null; message: string }[] } } })?.response?.data
  if (d?.deviations) return d.deviations.map((x) => `${x.oz || 'Summe'} · ${x.field}: erwartet ${String(x.expected)}, in der Datei ${String(x.actual)}`)
  if (d?.errors) return d.errors.map((x) => `${x.line ? `Zeile ${x.line}: ` : ''}${x.message}`)
  return []
}

/**
 * AP-37 Detailblatt K 3.9 — **Dialog „Ausgeben"**: Format und Fassung des Eingangs
 * (K-10, nicht wählbar), Angebotsdatum, Bieternummer, USt (0 % nur mit Umkehr der
 * Steuerschuld, K-18), Nachlass mit seiner Ebene (3.6), Positionen ohne Preis mit
 * Häkchen (K-11), Prüfliste, „als abgegeben kennzeichnen". Die Summen kommen aus
 * derselben Rechnung wie die Datei (Vorschau des Servers).
 */
export function LvAusgebenDialog({ kalkulation, onClose, onDone }: { kalkulation: Calculation; onClose: () => void; onDone: () => void }) {
  const { user } = useAuthStore()
  const darfKosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const [vorschau, setVorschau] = useState<LvExportVorschau | null>(null)
  const [datum, setDatum] = useState(heute())
  const [bieternummer, setBieternummer] = useState('')
  const [ust, setUst] = useState('20')
  const [umkehr, setUmkehr] = useState(false)
  const [ohnePreisOk, setOhnePreisOk] = useState(false)
  const [ohneNachlass, setOhneNachlass] = useState(false)
  const [abgeben, setAbgeben] = useState(true)
  // 3.9 — Beilagen mit derselben Abgabe-Kennung; Formblätter enthalten Kosten (cost.view).
  const [mitDruck, setMitDruck] = useState(false)
  const [mitK7, setMitK7] = useState(false)
  const [mitK3, setMitK3] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [details, setDetails] = useState<string[]>([])
  const [laeuft, setLaeuft] = useState(false)

  const ustZahl = Number(ust.replace(',', '.'))
  const optionen = useCallback(
    (): LvExportOptionen => ({
      vatPercent: Number.isFinite(ustZahl) ? ustZahl : 20,
      reverseCharge: umkehr,
      bidderNumber: bieternummer ? Number(bieternummer) : null,
      offerDate: datum || undefined,
      notOfferedConfirm: ohnePreisOk,
      withoutDiscount: ohneNachlass,
    }),
    [ustZahl, umkehr, bieternummer, datum, ohnePreisOk, ohneNachlass],
  )

  const laden = useCallback(async () => {
    try {
      const v = await previewLvExport(kalkulation.id, optionen())
      setVorschau(v)
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht rechnen.'))
    }
  }, [kalkulation.id, optionen])
  // Neu rechnen, wenn sich USt oder Nachlass ändern — Datum und Bieternummer ändern keine Summe.
  useEffect(() => {
    void laden()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kalkulation.id, ustZahl, ohneNachlass])
  useEffect(() => {
    if (vorschau && !vorschau.canSubmit) setAbgeben(false)
  }, [vorschau])

  const ausgeben = async () => {
    setLaeuft(true)
    setFehler(null)
    setDetails([])
    try {
      const a = await exportLv(kalkulation.id, { ...optionen(), markSubmitted: abgeben, withPrint: mitDruck, withForms: mitK7 || mitK3, withK3: mitK3 })
      await ladeAusgabe(kalkulation.id, a)
      onDone()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Ausgabe ist fehlgeschlagen.'))
      setDetails(fehlerDetails(e))
    } finally {
      setLaeuft(false)
    }
  }

  const v = vorschau
  const nachlassFehlt = !!v?.discount && !v.discount.allowed
  const befunde = v ? (Object.keys(PRUEFUNG) as LvCheckCode[]).filter((c) => (v.check.counts[c] ?? 0) > 0) : []
  const sperre =
    !v ||
    !v.supported ||
    laeuft ||
    (ustZahl === 0 && !umkehr) ||
    (v.withoutPrice.count > 0 && !ohnePreisOk) ||
    (nachlassFehlt && !ohneNachlass) ||
    (bieternummer !== '' && !(Number(bieternummer) >= 1 && Number(bieternummer) <= 999))

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">LV ausgeben</h2>
            <p className="text-xs text-muted-foreground">
              Angebots-LV in der Fassung der Ausschreibung. Die Datei wird selbst geprüft, gegen das Schema geprüft und als Nachweis gespeichert.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!v && !fehler && <p className="text-sm text-muted-foreground">Rechnet …</p>}
        {v && (
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <span className="block text-xs text-muted-foreground">Format</span>
                <span className="font-medium">
                  {v.format} {v.formatVersion}
                </span>
                {!v.supported && <span className="block text-xs text-destructive">Ausgegeben wird ONLV 2015 oder 2021 und GAEB DA XML 3.x.</span>}
                {v.generatedFrom && (
                  <span className="block text-xs text-amber-700 dark:text-amber-400">
                    Aus {v.generatedFrom === 'B2063' ? 'B 2063' : v.generatedFrom} erzeugt — in den Vorbemerkungen prüfen, welches Format abzugeben ist.
                    {v.notes.length > 0 && ` ${v.notes.join(' · ')}`}
                  </span>
                )}
                {v.format === 'GAEB' && (
                  <span className="block text-xs text-muted-foreground">
                    Angebotsabgabe ohne Texte, nur Preise und Textergänzungen{v.supported && !v.schemaChecked ? ' — ohne Schemaprüfung (erst ab 3.3)' : ''}.
                  </span>
                )}
                {!v.isTender && <span className="block text-xs text-amber-700 dark:text-amber-400">Das LV ist kein Ausschreibungs-LV ({v.lvKind}).</span>}
              </div>
              <label className="block">
                <span className="block text-xs text-muted-foreground">Angebotsdatum</span>
                <input type="date" className={inlineInput} value={datum} onChange={(e) => setDatum(e.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs text-muted-foreground">Bieternummer (falls vergeben)</span>
                <input className={inlineInput} inputMode="numeric" value={bieternummer} onChange={(e) => setBieternummer(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="—" />
              </label>
              <div>
                <label className="block">
                  <span className="block text-xs text-muted-foreground">USt-Satz %</span>
                  <input className={inlineInput} inputMode="decimal" value={ust} onChange={(e) => setUst(e.target.value)} />
                </label>
                {ustZahl === 0 && (
                  <label className="mt-1 flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={umkehr} onChange={(e) => setUmkehr(e.target.checked)} /> Umkehr der Steuerschuld
                  </label>
                )}
              </div>
            </div>

            <div className="rounded-md border border-border/60 p-3">
              <div className="mb-1 font-medium">Bieter</div>
              <div className="text-muted-foreground">
                {v.bidder.name || '—'}
                {v.bidder.city && ` · ${[v.bidder.street, `${v.bidder.zip ?? ''} ${v.bidder.city}`.trim()].filter(Boolean).join(', ')}`}
              </div>
              {!v.bidder.complete && <div className={`text-xs ${v.format === 'GAEB' ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'}`}>{v.format === 'GAEB' ? 'GAEB X84 verlangt die Anschrift — ' : ''}Anschrift unvollständig — Einstellungen der Kalkulation, Reiter LV (Bieterangaben).</div>}
            </div>

            {v.discount && (
              <div className="rounded-md border border-border/60 p-3">
                <div className="font-medium">
                  Nachlass {prozent(v.discount.percent)} %{' '}
                  {v.discount.allowed ? (
                    <span className="font-normal text-muted-foreground">
                      {EBENE[v.discount.level ?? ''] ?? ''}
                      {v.discount.perPriceShare ? ', je Preisanteil' : ''}
                    </span>
                  ) : null}
                </div>
                {nachlassFehlt && (
                  <label className="mt-1 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <input type="checkbox" checked={ohneNachlass} onChange={(e) => setOhneNachlass(e.target.checked)} /> Das LV lässt keinen Nachlass zu — ohne Nachlass ausgeben
                  </label>
                )}
              </div>
            )}

            {v.withoutPrice.count > 0 && (
              <div className="rounded-md border border-amber-500/40 p-3">
                <label className="flex items-center gap-2 font-medium">
                  <input type="checkbox" checked={ohnePreisOk} onChange={(e) => setOhnePreisOk(e.target.checked)} />
                  {v.withoutPrice.count} Position(en) ohne Preis als nicht angeboten ausgeben
                </label>
                <ul className="mt-1 max-h-24 overflow-y-auto text-xs text-muted-foreground">
                  {v.withoutPrice.items.map((p) => (
                    <li key={p.oz}>
                      <span className="tabular-nums">{p.oz}</span> {p.shortText}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {befunde.length > 0 && (
              <div className="text-xs">
                <span className="font-medium">Prüfliste: </span>
                {befunde.map((c) => `${PRUEFUNG[c].titel} ${v.check.counts[c]}`).join(' · ')}
              </div>
            )}

            <table className="w-full max-w-sm text-sm">
              <tbody>
                <tr>
                  <td className="text-muted-foreground">Summe des LV</td>
                  <td className="text-right tabular-nums">{geldText(v.totals.lvTotal)}</td>
                </tr>
                {v.totals.discounts !== 0 && (
                  <tr>
                    <td className="text-muted-foreground">Nachlass</td>
                    <td className="text-right tabular-nums">− {geldText(v.totals.discounts)}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted-foreground">Gesamtpreis</td>
                  <td className="text-right tabular-nums">{geldText(v.totals.net)}</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">USt {prozent(v.totals.vatPercent)} %</td>
                  <td className="text-right tabular-nums">{geldText(v.totals.vat)}</td>
                </tr>
                <tr className="border-t border-border font-semibold">
                  <td>Angebotspreis</td>
                  <td className="text-right tabular-nums">{geldText(v.totals.gross)}</td>
                </tr>
              </tbody>
            </table>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-xs text-muted-foreground">Beilagen:</span>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={mitDruck} onChange={(e) => setMitDruck(e.target.checked)} /> Druckbild (PDF)
              </label>
              {darfKosten && (
                <>
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={mitK7} onChange={(e) => setMitK7(e.target.checked)} /> Formblatt K7
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={mitK3} onChange={(e) => setMitK3(e.target.checked)} /> Formblatt K3
                  </label>
                </>
              )}
            </div>

            <label className={`flex items-center gap-2 ${v.canSubmit ? '' : 'text-muted-foreground'}`}>
              <input type="checkbox" checked={abgeben} disabled={!v.canSubmit} onChange={(e) => setAbgeben(e.target.checked)} />
              als abgegeben kennzeichnen — die Fassung wird festgeschrieben
            </label>
          </div>
        )}

        {fehler && <p className="mt-3 text-sm text-destructive">{fehler}</p>}
        {details.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-xs text-destructive">
            {details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={sperre}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            onClick={() => void ausgeben()}
          >
            <FileOutput className="h-4 w-4" /> {laeuft ? 'Gibt aus …' : 'Ausgeben'}
          </button>
        </div>
      </div>
    </div>
  )
}
